/**
 * Agent 节点组件
 *
 * 调用 Agent 执行任务的节点
 * 左侧输入连接点 + 右侧输出连接点
 */

import React, { memo, useMemo } from 'react';
import type { NodeProps } from '@xyflow/react';
import type { AgentNodeData, LoopNodeData } from '../../../types/workflow';
import { InputHandle, OutputHandle } from './CommonHandle';
import { useNodeExecutionStatus } from '../../../hooks/useNodeExecutionStatus';
import { useChapterRangeValidation } from '../../../hooks/useChapterRangeValidation';
import {
  cn,
  getNodeContainerClasses,
  getNodeHeaderClasses,
} from './nodeStyles';

const AgentNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as AgentNodeData;

  // 使用统一的执行状态 Hook
  const {
    executionStatus,
    isRunning,
    isCompleted,
    isFailed,
  } = useNodeExecutionStatus(id);

  // 使用章节范围验证 Hook
  const chapterValidation = useChapterRangeValidation(id);

  // 工具数量显示
  const toolCount = nodeData.enabled_tools?.length || 0;
  const toolSummary = toolCount > 0 ? `${toolCount} 个工具` : '未配置工具';

  // 章节范围显示（从父级循环容器继承）
  const allowedRange = chapterValidation.allowedRange;
  const chapterRangeText = useMemo(() => {
    if (allowedRange) {
      return `允许范围: ${allowedRange.start}-${allowedRange.end}`;
    }
    // 如果节点自身配置了章节范围（向后兼容）
    if (nodeData.chapter_range) {
      return `章节 ${nodeData.chapter_range.start}-${nodeData.chapter_range.end}`;
    }
    return null;
  }, [allowedRange, nodeData.chapter_range]);

  // 父级循环容器名称
  const parentLoopName = useMemo(() => {
    if (chapterValidation.parentLoopNode) {
      return (chapterValidation.parentLoopNode.data as LoopNodeData).label || '循环容器';
    }
    return null;
  }, [chapterValidation.parentLoopNode]);

  return (
    <div
      className={getNodeContainerClasses('agent', executionStatus, selected, isRunning)}
      data-node-id={id}
      data-node-type="agent"
      data-execution-status={executionStatus}
    >
      {/* 节点头部 */}
      <div className={getNodeHeaderClasses('agent')}>
        {/* 电脑图标 */}
        <svg className="w-4 h-4 text-paper-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="text-sm font-medium text-paper-700 flex-1 truncate">
          {nodeData.label || 'Agent'}
        </span>
        {/* Agent 名称徽章 */}
        {nodeData.agent_name && (
          <span className="text-xs bg-paper-100 text-paper-800 px-1.5 py-0.5 rounded">
            {nodeData.agent_name}
          </span>
        )}
      </div>

      {/* 节点内容 */}
      <div className="px-3 py-2 space-y-2">
        {/* Agent 名称 */}
        <div className={cn(
          'text-sm',
          nodeData.agent_id ? 'text-gray-700' : 'text-gray-400 italic',
        )}>
          {nodeData.agent_name || '请选择 Agent'}
        </div>

        {/* 工具配置 */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{toolSummary}</span>
        </div>

        {/* 章节范围限制（从循环容器继承） */}
        {chapterRangeText && (
          <div className={cn(
            'flex items-center gap-2 text-xs px-2 py-1 rounded',
            allowedRange
              ? 'text-purple-600 bg-purple-50 border border-purple-100'
              : 'text-gray-500 bg-gray-50'
          )}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium">{chapterRangeText}</span>
            {parentLoopName && (
              <span className="text-purple-400 text-[10px]">
                (来自 {parentLoopName})
              </span>
            )}
          </div>
        )}

        {/* 描述 */}
        {nodeData.description && (
          <div className="text-xs text-gray-400">
            {nodeData.description}
          </div>
        )}
      </div>

      {/* 输入连接点（左侧） */}
      <InputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />

      {/* 输出连接点（右侧） */}
      <OutputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />
    </div>
  );
};

AgentNodeComponent.displayName = 'AgentNode';

export const AgentNode = memo(AgentNodeComponent);
export default AgentNode;
